import {Component, Input, OnChanges, SimpleChanges, OnInit, AfterViewChecked, Output, EventEmitter, ElementRef, HostListener} from '@angular/core';

// document-highlighter.interfaces.ts OR within the component file

/**
 * Input structure describing what to highlight.
 */
export interface HighlightInfo {
  id: string;
  textToFind: string | RegExp; // The string or RegExp to search for
  cssClass?: string;
  tooltip?: string;
  data?: {
    originalText?: string; // The original, unmodified text for display
    [key: string]: any; // Allow other properties
  };
}

/**
 * Internal structure representing a processed text segment for rendering.
 */
export interface HighlightSegment {
  text: string;             // The chunk of text for this segment
  isHighlighted: boolean;   // True if this segment should be visually highlighted
  highlightInfo?: HighlightInfo; // Reference to the original HighlightInfo if highlighted
}

@Component({
  selector: 'app-document-highlighter',
  templateUrl: './document-highlighter.component.html',
  styleUrls: ['./document-highlighter.component.scss']
})
export class DocumentHighlighterComponent implements OnChanges, OnInit, AfterViewChecked {

  // --- Inputs ---
  @Input() textContent: string | null = null;
  @Input() highlights: HighlightInfo[] | null = null;
  @Input() allowManualHighlighting = false; // <-- ADDED Input

  // --- Outputs ---
  @Output() segmentClicked = new EventEmitter<HighlightSegment>();
  @Output() textSelected = new EventEmitter<{ text: string; contextElement: HTMLElement }>(); // <-- ADDED Output
  @Output() highlightRemoved = new EventEmitter<HighlightInfo>(); // <-- ADDED Output

  // --- Internal State ---
  processedSegments: HighlightSegment[] = [];
  selectedSegment: HighlightSegment | null = null;
  private pendingScrollToHighlight = false;

  constructor(private elementRef: ElementRef) { }

  ngOnInit(): void {
    this.processContent();
  }

  ngAfterViewChecked(): void {
    if (this.pendingScrollToHighlight) {
      this.pendingScrollToHighlight = false;
      this.scrollToFirstHighlight();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['textContent'] || changes['highlights']) {
      this.selectedSegment = null;
      this.processContent();
    }
  }

  /**
   * Processes the textContent based on the highlights input.
   */
  private processContent(): void {
    this.processedSegments = [];

    if (!this.textContent) {
      console.warn('DocumentHighlighter: No textContent provided.');
      return;
    }

    if (!this.highlights || this.highlights.length === 0) {
      this.processedSegments.push({ text: this.textContent, isHighlighted: false });
      return;
    }

    const allMatches: { start: number; end: number; highlightInfo: HighlightInfo }[] = [];

    this.highlights.forEach(highlightInfo => {
      // ======================= START: MODIFIED BLOCK =======================
      let regex: RegExp;
      const textToFind = highlightInfo.textToFind;

      if (textToFind instanceof RegExp) {
        // It's already a RegExp from the parent, use it directly.
        // We must ensure the global 'g' flag is set for the while loop to work correctly.
        if (!textToFind.flags.includes('g')) {
          console.warn('DocumentHighlighter: Provided RegExp is missing the global "g" flag. Highlighting may be incomplete.', textToFind);
          // You could recreate it, but it's better to enforce it in the parent.
          regex = new RegExp(textToFind.source, textToFind.flags + 'g');
        } else {
          regex = textToFind;
        }
        // Reset lastIndex for subsequent runs within the same component instance
        regex.lastIndex = 0;

      } else if (typeof textToFind === 'string' && textToFind) {
        // It's a string, use the existing logic to create a new RegExp.
        const escapedText = textToFind.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        regex = new RegExp(escapedText, 'g');

      } else {
        // Invalid input, skip this highlight.
        console.warn(`DocumentHighlighter: Skipping highlight with invalid textToFind:`, highlightInfo);
        return; // Skips to the next forEach iteration
      }

      try {
        let match;
        while ((match = regex.exec(this.textContent!)) !== null) {
          // This is to avoid infinite loops with zero-width matches
          if (match.index === regex.lastIndex) {
            regex.lastIndex++;
          }

          // CRITICAL FIX: The end position is the start + the length of the *actual matched string* (match[0]).
          const matchEnd = match.index + match[0].length;

          allMatches.push({
            start: match.index,
            end: matchEnd,
            highlightInfo: highlightInfo
          });
        }
      } catch (e) {
        console.error(`DocumentHighlighter: Error during RegExp matching for`, highlightInfo.textToFind, e);
      }
      // ======================= END: MODIFIED BLOCK =======================
    });

    if (allMatches.length === 0) {
      this.processedSegments.push({ text: this.textContent, isHighlighted: false });
      return;
    }

    allMatches.sort((a, b) => {
      if (a.start !== b.start) {
        return a.start - b.start;
      }
      return b.end - a.end;
    });

    const nonOverlappingMatches = this.resolveOverlappingMatches(allMatches);
    let lastEnd = 0;

    for (const match of nonOverlappingMatches) {
      if (match.start > lastEnd) {
        this.processedSegments.push({
          text: this.textContent.substring(lastEnd, match.start),
          isHighlighted: false
        });
      }
      this.processedSegments.push({
        text: this.textContent.substring(match.start, match.end),
        isHighlighted: true,
        highlightInfo: match.highlightInfo
      });
      lastEnd = match.end;
    }

    if (lastEnd < this.textContent.length) {
      this.processedSegments.push({
        text: this.textContent.substring(lastEnd),
        isHighlighted: false
      });
    }

    // Schedule scroll to first highlight after view renders
    if (this.processedSegments.some(s => s.isHighlighted)) {
      this.pendingScrollToHighlight = true;
    }
  }

  /**
   * Resolves overlapping matches from a sorted list.
   * Prioritizes the first-occurring match. If matches start at the same point,
   * the one that was sorted first (e.g., the longer one if sorted that way) wins.
   */
  private resolveOverlappingMatches(sortedMatches: { start: number; end: number; highlightInfo: HighlightInfo }[]): { start: number; end: number; highlightInfo: HighlightInfo }[] {
    if (!sortedMatches || sortedMatches.length === 0) {
      return [];
    }

    const result: { start: number; end: number; highlightInfo: HighlightInfo }[] = [];
    let lastMatchEnd = -1; // Tracks the end position of the last added match

    for (const currentMatch of sortedMatches) {
      // If the current match starts after or exactly where the last one ended
      if (currentMatch.start >= lastMatchEnd) {
        result.push(currentMatch);
        lastMatchEnd = currentMatch.end;
      }
      // Implicitly skip matches that start before the last one ended (overlap)
    }

    return result;
  }

  private scrollToFirstHighlight(): void {
    const el: HTMLElement = this.elementRef.nativeElement;
    const firstHighlight = el.querySelector('.highlighted') as HTMLElement;
    if (!firstHighlight) return;

    // Find the scrollable parent (modal body or document wrapper)
    const scrollParent = firstHighlight.closest('.document-content-wrapper')
      || firstHighlight.closest('.modal-body-content')
      || firstHighlight.closest('.modal-body');

    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      const highlightRect = firstHighlight.getBoundingClientRect();
      const offset = highlightRect.top - parentRect.top - 80; // 80px padding from top
      scrollParent.scrollTop += offset;
    } else {
      firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Helper to get dynamic CSS classes for a segment
  getSegmentClasses(segment: HighlightSegment): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = {
      'highlighted': segment.isHighlighted,
      'selected': segment === this.selectedSegment, // Check for object identity
      'clickable': segment.isHighlighted // Only highlighted segments are clickable for removal/info
    };
    // Add custom class from HighlightInfo if present
    if (segment.isHighlighted && segment.highlightInfo?.cssClass) {
      segment.highlightInfo.cssClass.split(' ').forEach(cls => {
        if (cls) classes[cls] = true;
      });
    }
    // Add class if manual highlighting is allowed (e.g., for cursor style)
    classes['manual-highlight-enabled'] = this.allowManualHighlighting; // <-- ADDED CLASS BINDING
    return classes;
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (!this.allowManualHighlighting) {
      return; // Only proceed if manual highlighting is enabled
    }

    const selection = window.getSelection();

    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return; // No selection or selection is empty
    }

    // --- IMPORTANT: Check if the selection is within *this* component's text display area ---
    const textDisplayElement = this.elementRef.nativeElement.querySelector('.highlighted-content');
    if (!textDisplayElement) return; // Should not happen if rendered

    const range = selection.getRangeAt(0);
    // Check if the selection starts or ends outside the target area
    if (!textDisplayElement.contains(range.startContainer) || !textDisplayElement.contains(range.endContainer)) {
      // Optional: Clear selection if it spans outside the desired area?
      // selection.removeAllRanges(); // Uncomment if you want to cancel selections spanning outside
      return;
    }
    // --- End Check ---

    const selectedText = selection.toString().trim();

    // Avoid triggering on simple clicks that might select tiny whitespace
    // Allow single character selection if needed, adjust length check accordingly
    if (selectedText && selectedText.length > 0) {
      // Find the specific span the selection *primarily* occurred within, if possible.
      // This is complex. For now, let's just provide the selected text and the container.
      // A more sophisticated approach might try to find the exact start/end offsets
      // relative to the original textContent.
      this.textSelected.emit({ text: selectedText, contextElement: textDisplayElement });

      // Optional: Clear the browser's selection visually *after* emitting,
      // so the parent can process it before it disappears. The parent should ideally do this.
      // setTimeout(() => selection.removeAllRanges(), 50); // Small delay
    }
  }

  /**
   * Handles clicks on segments.
   * If highlighted, emits segmentClicked.
   * Allows removal via Ctrl+Click if highlighted.
   */
  onSegmentClick(segment: HighlightSegment, event: MouseEvent): void { // Accept event
    if (segment.isHighlighted) {
      this.selectedSegment = segment; // Keep selection logic
      this.segmentClicked.emit(segment); // Emit standard click

      // --- REMOVAL LOGIC: Check for Ctrl key and valid info ---
      if (event.ctrlKey && segment.highlightInfo) {
        event.preventDefault(); // Prevent any default browser action (like context menu)
        this.removeHighlight(segment.highlightInfo);
      }
      // ------------------------------------------------------

    } else {
      // Clicked on non-highlighted text
      this.selectedSegment = null;
      // Optionally emit something if needed for non-highlighted clicks
    }
  }

  /** Helper to emit removal request with confirmation */
  private removeHighlight(highlightInfo: HighlightInfo): void {
    // Use the clean originalText if available, otherwise fallback to the textToFind property.
    const textForDisplay = highlightInfo.data?.originalText || String(highlightInfo.textToFind);

    if (confirm(`Remove highlight for "${textForDisplay}"?`)) {
      this.highlightRemoved.emit(highlightInfo);
      this.selectedSegment = null;
    }
  }
}
